import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { StepShell } from '@/components/signup/StepShell';
import { SignupCheckEmail } from '@/components/signup/SignupCheckEmail';
import { Field, useAutoFocus } from '@/components/booking/fields';
import { formatPhone } from '@/pages/Register';
import { Check, Eye, EyeOff } from 'lucide-react';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const STEPS = ['name', 'email', 'phone', 'company', 'password'];
const DRAFT_KEY = 'rc_booking_account_draft';
const loadDraft = () => { try { return JSON.parse(localStorage.getItem(DRAFT_KEY)) || {}; } catch { return {}; } };

const pwRules = (p) => [
  { met: p.length >= 8, text: 'At least 8 characters' },
  { met: /[A-Z]/.test(p), text: 'An uppercase letter' },
  { met: /[a-z]/.test(p), text: 'A lowercase letter' },
  { met: /\d/.test(p), text: 'A number' },
];

export const MobileAccountSetup = ({ returnTo, onExit, offset = 0, progressTotal }) => {
  const { register } = useAuth();
  const [form, setForm] = useState(() => ({ fullName: '', email: '', phone: '', company: '', ...loadDraft() }));
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [doneEmail, setDoneEmail] = useState('');
  const ref = useAutoFocus(index);

  const key = STEPS[index];
  const set = (k, v) => {
    const next = { ...form, [k]: v };
    setForm(next);
    localStorage.setItem(DRAFT_KEY, JSON.stringify(next));
    setError('');
  };
  const go = (d) => { setError(''); setDirection(d); setIndex((i) => i + d); };

  const validate = () => {
    if (key === 'name' && form.fullName.trim().length < 2) return 'Please enter your full name';
    if (key === 'email' && !EMAIL_RE.test(form.email.trim())) return 'Please enter a valid email address, like you@brokerage.com';
    if (key === 'phone' && form.phone.replace(/\D/g, '').length !== 10) return 'Please enter a 10-digit phone number';
    if (key === 'password') {
      const missing = pwRules(password).filter((r) => !r.met).map((r) => r.text.toLowerCase());
      if (missing.length) return `Your password still needs ${missing.join(', ')}`;
    }
    return null;
  };

  const createAccount = async () => {
    setLoading(true);
    const email = form.email.toLowerCase().trim();
    const result = await register(email, password, returnTo);
    if (result.success) {
      localStorage.setItem('rc_pending_profile', JSON.stringify({
        full_name: form.fullName.trim(), company: form.company.trim() || null,
        phone: form.phone, redirect_after: returnTo,
      }));
      localStorage.removeItem(DRAFT_KEY);
      setDoneEmail(email);
    } else {
      setError(result.error || 'We could not create your account. Please try again.');
    }
    setLoading(false);
  };

  const next = () => {
    const err = validate();
    if (err) { setError(err); return; }
    if (index === STEPS.length - 1) createAccount();
    else go(1);
  };

  if (doneEmail) return <SignupCheckEmail email={doneEmail} returnToParam={returnTo} redirectAfter={returnTo} />;

  const loginLink = `/login?returnTo=${encodeURIComponent(returnTo)}`;
  const screens = {
    name: {
      title: "Let's get you booked. What's your name?",
      hint: 'Last step — a quick free account so you can track your order and download your media. Your answers are saved.',
      footer: <>Already have an account? <Link to={loginLink} className="text-[#C6A14A] font-medium" data-testid="mobile-booking-login-link">Log in</Link></>,
      body: <Field ref={ref} id="account-name" label="Full name" required autoComplete="name" autoCapitalize="words" placeholder="Jane Smith" value={form.fullName} onChange={(e) => set('fullName', e.target.value)} />,
    },
    email: {
      title: "What's your email?", hint: "We'll send your confirmation and gallery here.",
      body: <Field ref={ref} id="account-email" label="Email" required type="email" inputMode="email" autoComplete="email" autoCapitalize="none" placeholder="you@brokerage.com" value={form.email} onChange={(e) => set('email', e.target.value)} />,
    },
    phone: {
      title: 'Best phone number?', hint: 'For shoot-day updates.',
      body: <Field ref={ref} id="account-phone" label="Mobile phone" required type="tel" inputMode="tel" autoComplete="tel" placeholder="(928) 555-1234" value={form.phone} onChange={(e) => set('phone', formatPhone(e.target.value))} />,
    },
    company: {
      title: 'Which brokerage are you with?', hint: 'Optional — helps us brand your media.', optional: true,
      body: <Field ref={ref} id="account-company" label="Company / brokerage" autoComplete="organization" placeholder="e.g. RE/MAX, Keller Williams" value={form.company} onChange={(e) => set('company', e.target.value)} />,
    },
    password: {
      title: 'Create a password', hint: `For ${form.email.trim()}`, continueLabel: 'Create Account',
      body: (
        <>
          <div className="relative">
            <Field ref={ref} id="account-password" label="Password" required type={show ? 'text' : 'password'} autoComplete="new-password" placeholder="Create a password" value={password} onChange={(e) => { setPassword(e.target.value); setError(''); }} />
            <button type="button" onClick={() => setShow((s) => !s)} aria-label={show ? 'Hide password' : 'Show password'}
              className="absolute right-2 bottom-1 h-12 w-12 flex items-center justify-center text-white/50 hover:text-white" data-testid="mobile-account-toggle-password">
              {show ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
          <ul className="space-y-1.5" data-testid="mobile-password-rules">
            {pwRules(password).map((r) => (
              <li key={r.text} className={`flex items-center gap-2 text-sm transition-colors ${r.met ? 'text-emerald-400' : 'text-white/40'}`}>
                <Check className="h-4 w-4" />{r.text}
              </li>
            ))}
          </ul>
        </>
      ),
    },
  };
  const screen = screens[key];

  return (
    <StepShell wide eyebrow="Last step" step={offset + index + 1} total={progressTotal || offset + STEPS.length} progressOnly={!!progressTotal} direction={direction} title={screen.title} hint={screen.hint}
      error={error} onBack={index > 0 ? () => go(-1) : onExit || null} onSubmit={next} loading={loading}
      onSkip={screen.optional ? () => { set('company', ''); go(1); } : null}
      continueLabel={screen.continueLabel} footer={screen.footer}>
      {screen.body}
    </StepShell>
  );
};
